<template>
  <div class="photo-upload">
    <div class="photo-grid">
      <div v-for="(url, index) in photos" :key="url" class="photo-thumb">
        <img :src="url" :alt="`${$t('title.photo')} ${index + 1}`" />
        <button
          type="button"
          class="photo-zoom"
          :aria-label="$t('button.enlargePhoto')"
          @click="openPreview(url)"
        >
          <b-icon icon="search-plus" />
        </button>
        <button
          type="button"
          class="delete photo-delete"
          :aria-label="$t('button.deletePhoto')"
          :disabled="isBusy"
          @click="confirmDelete(url)"
        ></button>
      </div>
      <button
        v-if="canAdd"
        type="button"
        class="photo-add"
        :class="{ 'is-disabled': isBusy }"
        :disabled="isBusy"
        @click="addPhoto"
      >
        <b-icon icon="camera" />
        <span>{{ $t('button.addPhoto') }}</span>
      </button>
      <!-- Two inputs: a phone's file picker does not always offer the camera, and capture
           on the only input would take the gallery away. The choice is ours to present -->
      <input
        ref="cameraInput"
        type="file"
        class="photo-input"
        accept="image/*"
        capture="environment"
        tabindex="-1"
        aria-hidden="true"
        :disabled="isBusy"
        @change="onFileChange"
      />
      <input
        ref="galleryInput"
        type="file"
        class="photo-input"
        accept="image/*"
        tabindex="-1"
        aria-hidden="true"
        :disabled="isBusy"
        @change="onFileChange"
      />
    </div>
    <b-progress
      v-if="uploadProgress !== null"
      class="photo-progress"
      type="is-info"
      size="is-small"
      :value="uploadProgress"
      show-value
    >
      {{ uploadProgress }}%
    </b-progress>
  </div>
</template>

<script>
import axios from 'axios'
import ApiService from '../../_api/api.service'
import PhotoPreview from '../../components/PhotoPreview.vue'
import ImageService from '../../_helpers/image.service'
import JwtService from '../../_helpers/jwt.service'
import ToastService from '../../_helpers/toast.service'

// heic is what a phone hands over when the browser cannot turn the photo into a jpeg; the
// api takes it and cloudinary stores it as a jpeg. Same list as the api's photoUpload config
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif'
]
// Same limit as the api's photoUpload config. A heic photo the browser could not convert
// travels at its original size, so the limit leaves room for one
const MAX_FILE_SIZE_MB = 5
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024
// A hanging request would keep the answer from being saved. An upload is cancelled once
// sending stalls for this long; a slow upload that keeps progressing is never cut off
const UPLOAD_STALL_TIMEOUT = 60 * 1000
// Once every byte is sent the api is still storing the photo, so its response gets longer
const UPLOAD_RESPONSE_TIMEOUT = 120 * 1000
const DELETE_TIMEOUT = 60 * 1000
// POST /answer has no timeout, so a save that hangs stops blocking the next one after this
const SAVE_WAIT = 60 * 1000
// The api refuses to save a photo 20 hours after its upload (unsavedPhotoMaxAgeHours), as its
// cleanup job deletes unsaved photos after 24. Measured on this device's clock from the end of
// the upload, so a device clock that is off does not matter
const UNSAVED_PHOTO_MAX_AGE = 18 * 60 * 60 * 1000

export default {
  props: {
    value: { type: Array, default: () => [] },
    questionId: { type: [Number, String], required: true },
    answerSetId: { type: [Number, String], required: true },
    maxPhotos: { type: Number, default: 3 }
  },
  data() {
    return {
      photos: [...(this.value || [])],
      // Requests are counted per answer, so an upload left running on the previous
      // question neither locks the new question nor shows it a foreign progress bar
      pending: {},
      progress: {}
    }
  },
  created() {
    // Photos uploaded here that no saved answer holds yet, by answer. They would stay in
    // Cloudinary until the server's cleanup job, so they are deleted once the user leaves the
    // question. Neither is rendered, so they stay out of data
    this.unsaved = {}
    // When each photo uploaded here finished uploading, by url, until it is known to be saved
    this.uploadedAt = {}
    // Photos of a save whose result is unknown, url to answer key: they may be saved, so they
    // are never deleted from here
    this.maybeSaved = {}
    // Answers whose POST /answer is running; their photos wait for the result
    this.saving = {}
    // When the latest save of each answer started
    this.savingSince = {}
    this.isDestroyed = false
    // A reload, a closed tab or a full page navigation skips the cleanup below, so the
    // browser asks first. The server removes whatever is left behind in the end
    this.isGuardingLeave = false
    this.onBeforeUnload = event => {
      // A 401 clears the session and redirects to the login page. Asking there would only
      // let the user stay on a page whose every request fails
      if (!JwtService.getToken()) return
      event.preventDefault()
      event.returnValue = ''
    }
  },
  beforeDestroy() {
    this.isDestroyed = true
    this.discardUnsaved(Object.keys(this.unsaved))
    this.updateLeaveGuard()
  },
  computed: {
    answerKey() {
      return `${this.answerSetId}:${this.questionId}`
    },
    canAdd() {
      return this.photos.length < this.maxPhotos
    },
    isBusy() {
      return Boolean(this.pending[this.answerKey])
    },
    uploadProgress() {
      const progress = this.progress[this.answerKey]
      return progress === undefined ? null : progress
    }
  },
  watch: {
    value: 'syncPhotos',
    // An unanswered question has no photos, so value stays undefined and only these change
    questionId: 'syncPhotos',
    answerSetId: 'syncPhotos',
    answerKey(newKey, oldKey) {
      this.discardUnsaved([oldKey])
    }
  },
  methods: {
    syncPhotos() {
      this.photos = Array.isArray(this.value) ? [...this.value] : []
    },
    isSameAnswer(questionId, answerSetId) {
      return (
        !this.isDestroyed &&
        questionId === this.questionId &&
        answerSetId === this.answerSetId
      )
    },
    addUnsaved(key, questionId, answerSetId, url) {
      const entry = this.unsaved[key] || { questionId, answerSetId, urls: [] }
      entry.urls.push(url)
      this.unsaved[key] = entry
      this.uploadedAt[url] = Date.now()
      this.updateLeaveGuard()
    },
    forgetUnsaved(key, urls) {
      const entry = this.unsaved[key]
      if (!entry) return
      entry.urls = entry.urls.filter(url => !urls.includes(url))
      if (!entry.urls.length) delete this.unsaved[key]
      this.updateLeaveGuard()
    },
    // Once a photo is saved or gone its upload time no longer matters
    forgetUploads(urls) {
      urls.forEach(url => {
        delete this.uploadedAt[url]
        delete this.maybeSaved[url]
      })
    },
    // unsaved is not reactive, so every change to it or to pending calls this
    updateLeaveGuard() {
      const shouldGuard =
        !this.isDestroyed &&
        (Object.keys(this.unsaved).length > 0 ||
          Object.keys(this.pending).length > 0)
      if (shouldGuard === this.isGuardingLeave) return
      this.isGuardingLeave = shouldGuard
      if (shouldGuard) {
        window.addEventListener('beforeunload', this.onBeforeUnload)
      } else {
        window.removeEventListener('beforeunload', this.onBeforeUnload)
      }
    },
    // The page asks before it saves. A photo still uploading would be left out of the answer
    // and deleted once it arrives, and one being deleted would be written back as a broken link
    canSave() {
      if (this.isBusy) {
        this.warn('description.photoBusy')
        return false
      }
      // Coming back to the question during a save shows the page's copy from before it, so a
      // second save could leave out photos the first one is storing
      if (
        this.saving[this.answerKey] &&
        Date.now() - this.savingSince[this.answerKey] < SAVE_WAIT
      ) {
        this.warn('description.answerStillSaving')
        return false
      }
      return !this.dropExpiredPhotos()
    },
    // The api would refuse the whole answer over an expired photo, and the user could not tell
    // which one it is, so those photos leave the list before the save. The save is stopped so
    // the answer is not stored without photos the user meant to keep
    dropExpiredPhotos() {
      const { answerKey, questionId, answerSetId } = this
      // A save still running may hold these photos; the api answers for it
      if (this.saving[answerKey]) return false
      const now = Date.now()
      const isExpired = url =>
        url in this.uploadedAt &&
        now - this.uploadedAt[url] >= UNSAVED_PHOTO_MAX_AGE
      const entry = this.unsaved[answerKey]
      const expired = entry ? entry.urls.filter(isExpired) : []
      if (expired.length) {
        this.forgetUnsaved(answerKey, expired)
        this.forgetUploads(expired)
        this.photos = this.photos.filter(photo => !expired.includes(photo))
        this.$emit('input', this.photos)
        expired.forEach(url =>
          this.removePhoto(url, questionId, answerSetId).catch(() => {})
        )
        this.warn('description.unsavedPhotosExpired')
        return true
      }
      // A photo that may be saved is missing from the list when the page's own copy of the
      // answer never got it, and saving without it would take it off the answer. Once expired,
      // dropping it could do the same, and keeping it gets the whole answer refused if it was
      // not saved. A reload shows which it is
      const uncertain = Object.keys(this.maybeSaved).some(
        url =>
          this.maybeSaved[url] === answerKey &&
          (!this.photos.includes(url) || isExpired(url))
      )
      if (uncertain) {
        this.warn('description.photoSaveUncertain')
        return true
      }
      return false
    },
    // While the answer is being saved its photo list is fixed, for the same reasons
    isSaving() {
      if (!this.saving[this.answerKey]) return false
      this.warn('description.answerSaving')
      return true
    },
    warn(message) {
      ToastService.open({ type: 'is-warning', message: this.$t(message) })
    },
    // The page hands over its POST /answer request and the photos it sent. Until the request
    // settles the answer is left alone, as leaving the question meanwhile would otherwise
    // delete photos that are being saved
    trackSave(request, photos) {
      const { answerKey, questionId, answerSetId } = this
      this.saving[answerKey] = (this.saving[answerKey] || 0) + 1
      this.savingSince[answerKey] = Date.now()
      const settle = result => {
        if (result !== 'failed') this.forgetUnsaved(answerKey, photos)
        if (result === 'saved') {
          this.forgetUploads(photos)
          // The answer now holds exactly these photos. If the user came back to it during the
          // save, the list shows the page's older copy, which the next save would write back
          if (
            this.isSameAnswer(questionId, answerSetId) &&
            (this.photos.length !== photos.length ||
              photos.some(url => !this.photos.includes(url)))
          ) {
            this.photos = [...photos]
            this.$emit('input', this.photos)
          }
        } else if (result === 'unknown') {
          photos
            .filter(url => url in this.uploadedAt)
            .forEach(url => {
              this.maybeSaved[url] = answerKey
            })
        }
        if (this.saving[answerKey] > 1) this.saving[answerKey]--
        else delete this.saving[answerKey]
        if (this.isDestroyed || answerKey !== this.answerKey) {
          this.discardUnsaved([answerKey])
        }
      }
      // The page handles the error. Only a 4xx means the api refused the answer. With no response
      // (a timeout or a lost connection) or a 5xx (such as Heroku's 503 when the dyno restarts)
      // the answer may have been saved, and deleting its photos would lose them; if it was not,
      // the server's cleanup job removes the files
      request.then(
        () => settle('saved'),
        error => {
          const response = error && error.isAxiosError && error.response
          const refused =
            !error || !error.isAxiosError || (response && response.status < 500)
          settle(refused ? 'failed' : 'unknown')
        }
      )
    },
    discardUnsaved(keys) {
      let discarded = false
      keys.forEach(key => {
        const entry = this.unsaved[key]
        if (!entry || this.saving[key]) return
        delete this.unsaved[key]
        this.forgetUploads(entry.urls)
        entry.urls.forEach(url => {
          discarded = true
          this.removePhoto(url, entry.questionId, entry.answerSetId).catch(
            () => {}
          )
        })
      })
      this.updateLeaveGuard()
      if (discarded) {
        ToastService.open({
          type: 'is-warning',
          duration: 4000,
          message: this.$t('description.unsavedPhotosDeleted')
        })
      }
    },
    photoLimitError() {
      if (this.photos.length >= this.maxPhotos) {
        return this.$t('description.photoLimitReached', { max: this.maxPhotos })
      }
      // The type and the size are known once the photo has been downscaled: a phone can hand
      // over a heic photo, which the browser turns into a jpeg on the way
      return null
    },
    // A pointer that cannot hover is a touch screen; capture is ignored on a desktop, where
    // the file dialog already covers everything
    hasCamera() {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(pointer: coarse)').matches
      )
    },
    openPicker(ref) {
      const input = this.$refs[ref]
      if (input) input.click()
    },
    addPhoto() {
      const limit = this.photoLimitError()
      if (limit) {
        ToastService.error(limit)
        return
      }
      if (!this.hasCamera()) {
        this.$refs.galleryInput.click()
        return
      }
      this.$buefy.dialog.confirm({
        title: this.$t('button.addPhoto'),
        message: this.$t('description.photoSourceQuestion'),
        confirmText: this.$t('button.takePhoto'),
        cancelText: this.$t('button.choosePhoto'),
        type: 'is-info',
        // the dialog is mounted on the body, so it outlives this component when the route
        // changes under it and the refs are gone by the time a button is pressed
        onConfirm: () => this.openPicker('cameraInput'),
        // buefy calls onCancel for the backdrop and for escape as well, and both of those
        // mean the user wants out, not the gallery
        onCancel: method => {
          if (method === 'button') this.openPicker('galleryInput')
        }
      })
    },
    onFileChange(event) {
      const input = event.target
      const file = input.files && input.files[0]
      // Lets the same file be picked again after an error
      input.value = ''
      if (!file || this.isSaving()) return

      const error = this.photoLimitError()
      if (error) {
        ToastService.error(error)
        return
      }
      this.upload(file)
    },
    startPending(key) {
      this.$set(this.pending, key, (this.pending[key] || 0) + 1)
      this.updateLeaveGuard()
    },
    endPending(key) {
      const count = (this.pending[key] || 1) - 1
      if (count > 0) this.$set(this.pending, key, count)
      else this.$delete(this.pending, key)
      this.updateLeaveGuard()
    },
    onUploadProgress(key, event) {
      if (event.lengthComputable && event.total) {
        this.$set(
          this.progress,
          key,
          Math.round((event.loaded * 100) / event.total)
        )
      }
    },
    async upload(file) {
      const { questionId, answerSetId, answerKey } = this
      this.$set(this.progress, answerKey, 0)
      this.startPending(answerKey)
      let stallTimer
      try {
        const photo = await ImageService.downscale(file)
        // A heic photo from a phone comes back as a jpeg; what the browser could not turn
        // into one is refused here, with the same message as before
        if (!ALLOWED_TYPES.includes(photo.data.type)) {
          ToastService.error(this.$t('description.photoInvalidType'))
          return
        }
        if (photo.size > MAX_FILE_SIZE) {
          ToastService.error(
            this.$t('description.photoTooLarge', { size: MAX_FILE_SIZE_MB })
          )
          return
        }

        const formData = new FormData()
        formData.append('questionId', questionId)
        formData.append('answerSetId', answerSetId)
        formData.append('file', photo.data, photo.name)

        const cancelSource = axios.CancelToken.source()
        const watchStall = (timeout = UPLOAD_STALL_TIMEOUT) => {
          clearTimeout(stallTimer)
          stallTimer = setTimeout(cancelSource.cancel, timeout)
        }
        watchStall()
        const { url } = await ApiService.upload(
          '/photo/upload',
          formData,
          event => {
            // Cancelling while the api stores the photo leaves it behind with no url to
            // track it by, so that wait is only cut off when the response is long overdue
            const sent = event.lengthComputable && event.loaded >= event.total
            watchStall(sent ? UPLOAD_RESPONSE_TIMEOUT : UPLOAD_STALL_TIMEOUT)
            this.onUploadProgress(answerKey, event)
          },
          { cancelToken: cancelSource.token }
        )
        if (typeof url !== 'string' || !url) {
          throw new Error('The upload returned no photo url')
        }
        if (!this.isSameAnswer(questionId, answerSetId)) {
          // The question changed during the upload; no answer will ever hold the photo
          this.removePhoto(url, questionId, answerSetId).catch(() => {})
          return
        }
        this.photos = [...this.photos, url]
        this.addUnsaved(answerKey, questionId, answerSetId, url)
        this.$emit('input', this.photos)
      } catch (error) {
        // ApiService toasts request errors, a cancelled one included; anything else would fail
        // without a word
        if (!error || (!error.isAxiosError && !axios.isCancel(error))) {
          ToastService.error(this.$t('description.photoUploadError'))
        }
      } finally {
        clearTimeout(stallTimer)
        this.endPending(answerKey)
        this.$delete(this.progress, answerKey)
      }
    },
    confirmDelete(url) {
      this.$buefy.dialog.confirm({
        title: this.$t('title.deletingPhoto'),
        message: this.$t('description.deletingPhotoConfirmMessage'),
        confirmText: this.$t('button.deletePhoto'),
        cancelText: this.$t('button.cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deletePhoto(url)
      })
    },
    async deletePhoto(url) {
      // Checked here, not when the dialog opens, as a save may start while it is open
      if (this.isSaving()) return
      const { questionId, answerSetId, answerKey } = this
      this.startPending(answerKey)
      const dropPhoto = () => {
        this.forgetUnsaved(answerKey, [url])
        this.forgetUploads([url])
        if (this.isSameAnswer(questionId, answerSetId)) {
          this.photos = this.photos.filter(photo => photo !== url)
          this.$emit('input', this.photos)
        }
        // The api also drops a saved photo from its answer, and the page keeps its own copy
        // of the saved answers even after the question changed
        this.$emit('deleted', { url, questionId, answerSetId })
      }
      try {
        await this.removePhoto(url, questionId, answerSetId)
        dropPhoto()
      } catch (error) {
        // ApiService shows the error toast. With no response (a timeout or a lost connection)
        // the api may still have deleted the photo, and saving its url again would store a
        // broken link, so it is dropped all the same. A file left behind is only an orphan
        if (error && error.isAxiosError && !error.response) {
          dropPhoto()
          this.warn('description.photoDeleteUncertain')
        }
      } finally {
        this.endPending(answerKey)
      }
    },
    removePhoto(url, questionId, answerSetId) {
      return ApiService.delete('/photo', {
        data: { url, questionId, answerSetId },
        timeout: DELETE_TIMEOUT
      })
    },
    openPreview(url) {
      this.$buefy.modal.open({
        parent: this,
        component: PhotoPreview,
        props: { url }
      })
    }
  }
}
</script>

<style scoped>
/* Spaced like a form field. The children are flex items, so the grid's negative margin stays
   inside instead of collapsing into this margin (flow-root would do the same, but only from
   iOS 13 on) */
.photo-upload {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;
}

/* Takes back the bottom margin of the last row of photos */
.photo-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-bottom: -0.5rem;
}

.photo-thumb,
.photo-add {
  position: relative;
  font: inherit;
  color: inherit;
  background: none;
  -webkit-appearance: none;
  appearance: none;
  width: 80px;
  height: 80px;
  margin: 0 0.5rem 0.5rem 0;
  border-radius: 4px;
}

.photo-thumb {
  overflow: hidden;
  border: 1px solid #dbdbdb;
}

.photo-thumb img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-zoom {
  position: absolute;
  padding: 0;
  border: none;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: rgba(10, 10, 10, 0.4);
  opacity: 0;
  transition: opacity 0.15s;
  cursor: zoom-in;
}

.photo-thumb:hover .photo-zoom {
  opacity: 1;
}

/* Bulma sizes .delete at 20px, which is a small target next to the preview */
.photo-delete {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  max-width: 24px;
  max-height: 24px;
}

.photo-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border: 2px dashed #dbdbdb;
  color: #7a7a7a;
  font-size: 0.75rem;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
}

.photo-add:hover {
  border-color: #b5b5b5;
  color: #4a4a4a;
}

.photo-add.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Out of sight but still in the layout: display: none has a history of mobile browsers
   refusing to open the picker for it */
.photo-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.photo-progress {
  max-width: 260px;
  margin-top: 0.5rem;
}

/* Without hover the zoom hint would never show, so it stays visible on touch */
@media (hover: none) {
  .photo-zoom {
    opacity: 1;
    background: rgba(10, 10, 10, 0.25);
  }
}

@media screen and (max-width: 768px) {
  .photo-thumb,
  .photo-add {
    width: 70px;
    height: 70px;
  }

  .photo-delete {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    max-width: 28px;
    max-height: 28px;
  }
}
</style>
